import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox, Icon, Button } from 'tea-component';
import { getLocale, useIntl } from 'umi';
import { getEventName } from '../../../utils';
import { arrayToSimpleObj, sortTimeListFunc } from '@op/utils';
import CardCommon from '@op/components/CardCommon';
import './index.scss';
import { getRealXAxisValue } from '../../MetricsChart/utils';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { formatTimeToShow } from '@op/utils/time';
import { useSyncState } from '@op/hooks/common';
import { useUpdateEffect } from 'ahooks';
const defaultShowEvent = [
  'Version',
  'Game Show',
  'Marketing Events',
  'Announcement',
];

export default function EventSetting({
  visible,
  close,
  confirm,
  data,
  timeFormat,
  showEventAxisValue,
  showEventBubblesValue,
  dateType,
  xAxisData,
}: any) {
  const { formatMessage } = useIntl();
  const lang = getLocale();

  const {
    state: selectValue,
    setState: setSelectValue,
    syncStates: syncSelectValue,
    revertToPrevious: revertSelectValue,
    initStates: initSelectValue,
  } = useSyncState<any>({});

  const {
    state: showEventAxis,
    setState: setShowEventAxis,
    syncStates: syncShowEventAxis,
    revertToPrevious: revertShowEventAxis,
    initStates: initShowEventAxis,
  } = useSyncState<any>(showEventAxisValue);

  const {
    state: showEventBubbles,
    setState: setShowEventBubbles,
    syncStates: syncShowEventBubbles,
    revertToPrevious: revertShowEventBubbles,
    initStates: initShowEventBubbles,
  } = useSyncState<any>(showEventBubblesValue);

  useUpdateEffect(() => {
    initShowEventAxis(showEventAxisValue);
  }, [showEventAxisValue]);

  useUpdateEffect(() => {
    initShowEventBubbles(showEventBubblesValue);
  }, [showEventBubblesValue]);

  function closeWindow() {
    close();
    revertSelectValue();
    revertShowEventBubbles();
    revertShowEventAxis();
  }

  function syncAllStates() {
    syncSelectValue();
    syncShowEventAxis();
    syncShowEventBubbles();
  }

  const changeSelectValue = (value: any, key?: string) => {
    if (key) {
      setSelectValue({ ...selectValue, [key]: value });
    } else {
      setSelectValue(value);
    }
  };
  const [levelOneActive, setLevelOneActive] = useState('');
  const [levelOneOptions, setLevelOneOptions] = useState<
    Array<{
      name: string;
      name_cn: string;
      value: string;
      children: Array<string>;
    }>
  >([]);

  function getOnlyOneKey(item: any) {
    return (
      item.event_type + '$' + item.name + '$' + item.start_time + '$' + item.url
    );
  }

  function getCallbackEventList(selectValue: Array<string>) {
    const selectedList = Object.values(selectValue).flat(2);
    const list = Object.values(data)
      .flat(3)
      .filter((item) => selectedList.includes(getOnlyOneKey(item)));

    list.sort(sortTimeListFunc('start_time', 'desc'));
    const result: { [key: string]: Array<any> } = {};

    list.forEach((item: any) => {
      let time = getRealXAxisValue({
        xAxis: item.start_time,
        dateType,
        format: timeFormat,
        xAxisData,
      });
      if (!result[time]) {
        result[time] = [];
      }
      result[time].push(item);
    });

    return result;
  }

  // 初始化数据
  useEffect(() => {
    const selected: any = {};
    const levelOneOptions = Object.keys(data || {}).map((key) => {
      const list = data[key] || [];

      const item = list[0] || {};
      const children = list.map((item: any) => getOnlyOneKey(item));

      selected[key] = defaultShowEvent.includes(key) ? children : [];

      return {
        name: key,
        name_cn: item.event_type_cn || key,
        value: key,
        children,
      };
    });

    confirm({
      bubbleEvent: getCallbackEventList(selected),
      showEventAxis,
      showEventBubbles,
    });

    initSelectValue(selected);
    setLevelOneActive(levelOneOptions?.[0]?.value);
    setLevelOneOptions(levelOneOptions);
  }, [data]);

  // level one select数据
  const { levelOneSelect, levelOneIndeterminate, levelOneIsClear } =
    useMemo(() => {
      const result: any = {
        levelOneSelect: {},
        levelOneIndeterminate: {},
        levelOneIsClear: false,
      };

      for (const key in data) {
        const element = data[key] || [];
        const selected = selectValue[key] || [];

        if (element.length === selected.length) {
          result.levelOneSelect[key] = true;
          result.levelOneIsClear = true;
        } else if (selected.length > 0) {
          result.levelOneIndeterminate[key] = true;
          result.levelOneIsClear = true;
        } else {
          result.levelOneSelect[key] = false;
        }
      }

      return result;
    }, [selectValue, data]);
  const handleLevelOneToggleClearBtn = () => {
    if (levelOneIsClear) {
      return changeSelectValue(
        Object.keys(selectValue).reduce(
          (pre, key) => ({ ...pre, [key]: [] }),
          {},
        ),
      );
    }

    const item = arrayToSimpleObj(levelOneOptions, 'children', 'value');
    changeSelectValue(item);
  };

  // level two select数据
  const levelTwoOptions = useMemo(() => {
    const list = (data?.[levelOneActive] || []).map((item: any) => ({
      ...item,
      value: getOnlyOneKey(item),
    }));
    list.sort(sortTimeListFunc('start_time', 'desc'));
    return list;
  }, [levelOneActive, data]);
  const { levelTwoSelect, levelTwoIsClear } = useMemo(() => {
    const value = selectValue[levelOneActive] || [];
    return {
      levelTwoSelect: value,
      levelTwoIsClear: value.length > 0,
    };
  }, [selectValue, levelOneActive]);

  const handleLevelTwoToggleClearBtn = () => {
    if (levelTwoIsClear) {
      return changeSelectValue([], levelOneActive);
    }

    const item = levelOneOptions.find(
      (item: any) => item.value === levelOneActive,
    );
    return changeSelectValue(item?.children || [], levelOneActive);
  };

  return (
    <Modal
      className="dbo-modal-event-setting"
      visible={visible}
      caption={formatMessage({ id: 'Events Setting' })}
      onClose={() => closeWindow()}
    >
      <Modal.Body>
        <div className="dbo-event-setting dbc-region-select-container">
          <div className="dbo-event-setting-hd">
            <div className="dbo-event-setting-tips">
              {formatMessage({
                id: 'When too many events are selected, some may not be fully displayed on the chart due to space constraints. To view all the events, hover your mouse over the numbers below the axis.',
              })}
            </div>
            <Checkbox
              value={showEventAxis}
              onChange={(value) => {
                setShowEventAxis(value);
                buriedBtnPoint('KMLG_EventSetting_axis', value);
              }}
            >
              {formatMessage({ id: 'Show event axis' })}
            </Checkbox>
            <Checkbox
              value={showEventBubbles}
              onChange={(value) => {
                setShowEventBubbles(value);
                buriedBtnPoint('KMLG_EventSetting_Bubble', value);
              }}
            >
              {formatMessage({ id: 'Show event bubbles' })}
            </Checkbox>
          </div>
          <div className="dbo-event-setting-bd">
            <CardCommon
              contentCard
              empty={levelOneOptions.length === 0}
              emptyHeight={280}
              hasEmptyTitle={false}
              emptyDescText="There are no events scheduled for this period."
            >
              <div className="dbc-region-select-content-container">
                <div className="dbc-region-select-content-wrap">
                  <div className="dbc-region-select-content-title-wrap">
                    <span>{formatMessage({ id: 'Set display events' })}</span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelOneToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelOneIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    {levelOneOptions.map((item) => (
                      <div
                        key={item.value}
                        onClick={() => setLevelOneActive(item.value)}
                        className={`dbc-region-select-checkbox ${
                          levelOneActive === item.value ? 'active' : ''
                        }`}
                      >
                        <Checkbox
                          value={levelOneSelect[item.value]}
                          indeterminate={levelOneIndeterminate[item.value]}
                          onChange={(value) => {
                            setLevelOneActive(item.value);
                            const selected = value ? item.children : [];
                            changeSelectValue(selected, item.value);
                          }}
                        ></Checkbox>
                        <div className="dbc-region-select-checkbox-name-wrap">
                          <span className="dbc-region-select-checkbox-name">
                            {getEventName(item, lang)} ({item.children.length})
                          </span>
                          <Icon type="arrowright" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="dbc-region-select-content-wrap dbc-region-select-content-wrap--right">
                  <div className="dbc-region-select-content-title-wrap">
                    <span className="dbc-region-select-content-country-title">
                      {formatMessage({ id: 'Event' })}
                    </span>
                    <span
                      className="dbc-region-select-button"
                      onClick={handleLevelTwoToggleClearBtn}
                    >
                      {formatMessage({
                        id: levelTwoIsClear ? 'Clear All' : 'Select All',
                      })}
                    </span>
                  </div>
                  <div className="dbc-region-select-item-wrap">
                    <Checkbox.Group
                      value={levelTwoSelect}
                      onChange={(value) => {
                        changeSelectValue(value, levelOneActive);
                      }}
                    >
                      {levelTwoOptions.map((item: any) => {
                        return (
                          <Checkbox
                            name={item.value}
                            key={item.value}
                            className="dbc-region-select-checkbox"
                          >
                            <div className="dbc-region-select-checkbox-name-wrap">
                              <span className="dbc-region-select-checkbox-name">
                                {getEventName(item, lang)}
                              </span>
                              <span className="dbc-region-select-checkbox-date">
                                {formatTimeToShow({
                                  ...item,
                                  date_type: 'hourly',
                                  hourlyIsSame: true,
                                })}
                              </span>
                            </div>
                          </Checkbox>
                        );
                      })}
                    </Checkbox.Group>
                  </div>
                </div>
              </div>
            </CardCommon>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            syncAllStates();
            confirm({
              bubbleEvent: getCallbackEventList(selectValue),
              showEventAxis,
              showEventBubbles,
            });
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={() => closeWindow()}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
